import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { apiGet, apiPost } from "../../lib/api";

/**
 * Email record from API
 */
interface EmailRecord {
  id: string;
  emailType: string;
  sequenceName: string | null;
  sequencePosition: number | null;
  subject: string;
  sentAt: string;
  openedAt: string | null;
  clickedAt: string | null;
  bouncedAt: string | null;
  clicks: Array<{
    linkUrl: string;
    linkName: string | null;
    clickedAt: string;
  }>;
}

/**
 * Sequence state from API
 */
interface SequenceState {
  name: string;
  currentPosition: number;
  startedAt: string;
  nextSendAt: string | null;
  pausedAt: string | null;
  completedAt: string | null;
  exitedReason: string | null;
  status: "active" | "paused" | "completed" | "exited";
}

/**
 * Email preferences
 */
interface EmailPreferences {
  marketing: boolean;
  sequences: boolean;
  newsletter: boolean;
  transactional: boolean;
}

/**
 * API response type
 */
interface UserEmailHistoryResponse {
  emails: EmailRecord[];
  sequences: SequenceState[];
  preferences: EmailPreferences;
  profile: {
    email: string;
  };
}

/**
 * UserEmailHistoryPage - View email history for a specific user.
 *
 * Features:
 * - Complete email history with open/click tracking
 * - Sequence enrollment status with controls
 * - Email preference management
 */
export function UserEmailHistoryPage() {
  const { profileId } = useParams<{ profileId: string }>();
  const [data, setData] = useState<UserEmailHistoryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [expandedEmail, setExpandedEmail] = useState<string | null>(null);

  // Fetch user email data
  const fetchData = async () => {
    if (!profileId) return;

    setLoading(true);
    setError(null);

    try {
      const res = await apiGet<UserEmailHistoryResponse>(
        `/api/admin/users/${profileId}/emails`
      );

      if (res.error) {
        throw new Error(res.error.message);
      }

      setData(res.data ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load email history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [profileId]);

  // Sequence actions
  const handleSequenceAction = async (
    sequenceName: string,
    action: "pause" | "resume" | "skip" | "exit"
  ) => {
    if (!profileId) return;

    setActionLoading(`${sequenceName}-${action}`);

    try {
      const res = await apiPost<{ success: boolean }>(
        `/api/admin/users/${profileId}/sequences/${sequenceName}/${action}`,
        {}
      );

      if (res.error) {
        throw new Error(res.error.message);
      }

      // Refresh data
      await fetchData();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Action failed");
    } finally {
      setActionLoading(null);
    }
  };

  // Preference update
  const handlePreferenceChange = async (key: keyof EmailPreferences, value: boolean) => {
    if (!profileId) return;

    try {
      const res = await apiPost<{ success: boolean }>(
        `/api/admin/users/${profileId}/preferences`,
        { [key]: value }
      );

      if (res.error) {
        throw new Error(res.error.message);
      }

      // Update local state
      if (data) {
        setData({
          ...data,
          preferences: { ...data.preferences, [key]: value },
        });
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to update preference");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-magic-500 border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <p className="text-red-600 font-medium">Error loading email history</p>
        <p className="text-red-500 text-sm mt-1">{error}</p>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Email History</h1>
          <p className="mt-1 text-sm text-gray-500">
            {data.profile.email}
          </p>
        </div>
        <Link
          to={`/admin/users/${profileId}`}
          className="
            px-4 py-2 text-sm font-medium
            text-gray-700 bg-white border border-gray-200 rounded-lg
            hover:bg-gray-50 transition-colors inline-flex items-center gap-2
          "
        >
          <ArrowLeftIcon />
          Back to User
        </Link>
      </div>

      {/* Sequences Section */}
      {data.sequences.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Email Sequences
          </h2>
          <div className="space-y-4">
            {data.sequences.map((seq) => (
              <SequenceCard
                key={seq.name}
                sequence={seq}
                onAction={(action) => handleSequenceAction(seq.name, action)}
                actionLoading={actionLoading}
              />
            ))}
          </div>
        </div>
      )}

      {/* Email Preferences */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Email Preferences
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {(Object.keys(data.preferences) as Array<keyof EmailPreferences>).map((key) => (
            <PreferenceToggle
              key={key}
              label={formatPreferenceLabel(key)}
              enabled={data.preferences[key]}
              onChange={(value) => handlePreferenceChange(key, value)}
            />
          ))}
        </div>
      </div>

      {/* Email History Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Sent Emails ({data.emails.length})
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Subject
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Clicks
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.emails.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                    <EmailIcon />
                    <p className="mt-2 text-sm">No emails sent to this user</p>
                  </td>
                </tr>
              ) : (
                data.emails.map((email) => (
                  <EmailRow
                    key={email.id}
                    email={email}
                    isExpanded={expandedEmail === email.id}
                    onToggle={() =>
                      setExpandedEmail(expandedEmail === email.id ? null : email.id)
                    }
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Components

interface SequenceCardProps {
  sequence: SequenceState;
  onAction: (action: "pause" | "resume" | "skip" | "exit") => void;
  actionLoading: string | null;
}

function SequenceCard({ sequence, onAction, actionLoading }: SequenceCardProps) {
  const isLoading = (action: string) =>
    actionLoading === `${sequence.name}-${action}`;

  const statusColors = {
    active: "bg-green-100 text-green-700",
    paused: "bg-yellow-100 text-yellow-700",
    completed: "bg-blue-100 text-blue-700",
    exited: "bg-gray-100 text-gray-700",
  };

  return (
    <div className="p-4 rounded-lg bg-gray-50 border border-gray-100">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h3 className="font-semibold text-gray-900">
              {formatSequenceName(sequence.name)}
            </h3>
            <span className={`text-xs font-medium px-2 py-1 rounded-full ${statusColors[sequence.status]}`}>
              {sequence.status}
            </span>
          </div>
          <div className="mt-2 flex flex-wrap gap-4 text-sm text-gray-600">
            <span>
              Position: <strong>{sequence.currentPosition + 1}</strong>
            </span>
            <span>
              Started: {formatDate(sequence.startedAt)}
            </span>
            {sequence.nextSendAt && sequence.status === "active" && (
              <span>
                Next send: {formatDate(sequence.nextSendAt)}
              </span>
            )}
            {sequence.pausedAt && (
              <span>
                Paused: {formatDate(sequence.pausedAt)}
              </span>
            )}
            {sequence.completedAt && (
              <span>
                Completed: {formatDate(sequence.completedAt)}
              </span>
            )}
            {sequence.exitedReason && (
              <span>
                Exit reason: <em>{sequence.exitedReason}</em>
              </span>
            )}
          </div>
        </div>

        {/* Action buttons for active/paused sequences */}
        {(sequence.status === "active" || sequence.status === "paused") && (
          <div className="flex flex-wrap gap-2">
            {sequence.status === "active" && (
              <>
                <button
                  onClick={() => onAction("skip")}
                  disabled={isLoading("skip")}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  {isLoading("skip") ? "..." : "Skip Next"}
                </button>
                <button
                  onClick={() => onAction("pause")}
                  disabled={isLoading("pause")}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg bg-yellow-50 border border-yellow-200 text-yellow-700 hover:bg-yellow-100 disabled:opacity-50"
                >
                  {isLoading("pause") ? "..." : "Pause"}
                </button>
              </>
            )}
            {sequence.status === "paused" && (
              <button
                onClick={() => onAction("resume")}
                disabled={isLoading("resume")}
                className="px-3 py-1.5 text-xs font-medium rounded-lg bg-green-50 border border-green-200 text-green-700 hover:bg-green-100 disabled:opacity-50"
              >
                {isLoading("resume") ? "..." : "Resume"}
              </button>
            )}
            <button
              onClick={() => onAction("exit")}
              disabled={isLoading("exit")}
              className="px-3 py-1.5 text-xs font-medium rounded-lg bg-red-50 border border-red-200 text-red-700 hover:bg-red-100 disabled:opacity-50"
            >
              {isLoading("exit") ? "..." : "Exit Sequence"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

interface PreferenceToggleProps {
  label: string;
  enabled: boolean;
  onChange: (value: boolean) => void;
}

function PreferenceToggle({ label, enabled, onChange }: PreferenceToggleProps) {
  return (
    <label className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors">
      <input
        type="checkbox"
        checked={enabled}
        onChange={(e) => onChange(e.target.checked)}
        className="w-4 h-4 rounded border-gray-300 text-magic-600 focus:ring-magic-500"
      />
      <span className="text-sm font-medium text-gray-700">{label}</span>
    </label>
  );
}

interface EmailRowProps {
  email: EmailRecord;
  isExpanded: boolean;
  onToggle: () => void;
}

function EmailRow({ email, isExpanded, onToggle }: EmailRowProps) {
  const status = email.bouncedAt
    ? "bounced"
    : email.clickedAt
      ? "clicked"
      : email.openedAt
        ? "opened"
        : "sent";

  const statusStyles = {
    sent: "bg-gray-100 text-gray-700",
    opened: "bg-green-100 text-green-700",
    clicked: "bg-purple-100 text-purple-700",
    bounced: "bg-red-100 text-red-700",
  };

  return (
    <>
      <tr
        className="hover:bg-gray-50 cursor-pointer"
        onClick={onToggle}
      >
        <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
          {formatDate(email.sentAt)}
        </td>
        <td className="px-6 py-4 text-sm text-gray-900 max-w-xs">
          <div className="flex items-center gap-2">
            {email.clicks.length > 0 && (
              <ChevronIcon className={isExpanded ? "rotate-90" : ""} />
            )}
            <span className="truncate">{email.subject}</span>
          </div>
        </td>
        <td className="px-6 py-4">
          <span className="text-xs font-medium px-2 py-1 rounded-full bg-gray-100 text-gray-700">
            {formatEmailType(email.emailType)}
          </span>
          {email.sequenceName && (
            <span className="ml-2 text-xs text-gray-500">
              (#{email.sequencePosition !== null ? email.sequencePosition + 1 : "?"})
            </span>
          )}
        </td>
        <td className="px-6 py-4">
          <span className={`text-xs font-medium px-2 py-1 rounded-full ${statusStyles[status]}`}>
            {status}
          </span>
        </td>
        <td className="px-6 py-4 text-sm text-gray-500">
          {email.clicks.length > 0 ? (
            <span className="text-purple-600 font-medium">
              {email.clicks.length} {email.clicks.length === 1 ? "click" : "clicks"}
            </span>
          ) : (
            "—"
          )}
        </td>
      </tr>
      {/* Expanded click details */}
      {isExpanded && email.clicks.length > 0 && (
        <tr className="bg-gray-50">
          <td colSpan={5} className="px-6 py-4">
            <div className="ml-8 space-y-2">
              <p className="text-xs font-semibold text-gray-500 uppercase">
                Click History
              </p>
              {email.clicks.map((click, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-4 text-sm text-gray-600"
                >
                  <span className="text-gray-400">
                    {formatDate(click.clickedAt)}
                  </span>
                  <a
                    href={click.linkUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-magic-600 hover:underline truncate max-w-md"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {click.linkName || click.linkUrl}
                  </a>
                </div>
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// Helper functions
function formatEmailType(type: string): string {
  return type
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatSequenceName(name: string): string {
  return name
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatPreferenceLabel(key: string): string {
  return key.charAt(0).toUpperCase() + key.slice(1);
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

// Icons
function ArrowLeftIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
    </svg>
  );
}

function ChevronIcon({ className = "" }: { className?: string }) {
  return (
    <svg
      className={`w-4 h-4 text-gray-400 transition-transform ${className}`}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  );
}

function EmailIcon() {
  return (
    <svg
      className="w-12 h-12 text-gray-300 mx-auto"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
      />
    </svg>
  );
}

export default UserEmailHistoryPage;
